import { baseApi } from '@/store/baseApi';
import { CacheDurations } from '@web/utils';

import type {
  BulkDeletePeoplePayload,
  BulkDeletePeopleResponse,
  CreatePersonPayload,
  CreatePersonResponse,
  DeletePersonResponse,
  GetPeopleParams,
  GetPersonResponse,
  UpdatePersonPayload,
  UpdatePersonResponse,
} from '@/features/contacts/people/types';
import type { Person } from '@/features/contacts/types';
import type { APIResponse, PaginatedData } from '@web/types';

export const createPersonInvalidatesTags = () => [
  { type: 'People' as const, id: 'LIST' },
  { type: 'Companies' as const, id: 'LIST' },
];

export const deletePersonInvalidatesTags = (
  _result: unknown,
  _error: unknown,
  id: string,
) => [
  { type: 'People' as const, id },
  { type: 'People' as const, id: 'LIST' },
  { type: 'Companies' as const, id: 'LIST' },
];

export const updatePersonInvalidatesTags = (
  _result: unknown,
  _error: unknown,
  id: string,
) => [
  { type: 'People' as const, id },
  { type: 'People' as const, id: 'LIST' },
  { type: 'Companies' as const, id: 'LIST' },
];

export const bulkDeletePeopleInvalidatesTags = () => [
  { type: 'People' as const, id: 'LIST' },
  { type: 'Companies' as const, id: 'LIST' },
];

type UpdatePersonArgs = {
  personId: string;
  payload: UpdatePersonPayload;
};

export const peopleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPeople: builder.query<
      APIResponse<PaginatedData<Person>>,
      GetPeopleParams | void
    >({
      query: (params) => ({
        url: 'contacts/people',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.MEDIUM,
      providesTags: (result) =>
        result?.data?.results
          ? [
              ...result.data.results.map((person) => ({
                type: 'People' as const,
                id: person.id,
              })),
              { type: 'People' as const, id: 'LIST' },
            ]
          : [{ type: 'People' as const, id: 'LIST' }],
    }),
    getPerson: builder.query<APIResponse<GetPersonResponse>, string>({
      query: (personId) => ({
        url: `contacts/people/${personId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: (_result, _error, id) => [{ type: 'People', id }],
    }),
    createPerson: builder.mutation<
      APIResponse<CreatePersonResponse>,
      CreatePersonPayload
    >({
      query: (body) => ({
        url: 'contacts/people',
        method: 'POST',
        body,
      }),
      invalidatesTags: createPersonInvalidatesTags,
    }),
    updatePerson: builder.mutation<
      APIResponse<UpdatePersonResponse>,
      UpdatePersonArgs
    >({
      query: ({ personId, payload }) => ({
        url: `contacts/people/${personId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) =>
        updatePersonInvalidatesTags(_result, _error, args.personId),
    }),
    deletePerson: builder.mutation<APIResponse<DeletePersonResponse>, string>({
      query: (personId) => ({
        url: `contacts/people/${personId}`,
        method: 'DELETE',
      }),
      invalidatesTags: deletePersonInvalidatesTags,
    }),
    bulkDeletePeople: builder.mutation<
      APIResponse<BulkDeletePeopleResponse>,
      BulkDeletePeoplePayload
    >({
      query: (body) => ({
        url: 'contacts/people/bulk-delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: bulkDeletePeopleInvalidatesTags,
    }),
  }),
});

export const {
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useGetPeopleQuery,
  useLazyGetPeopleQuery,
  useGetPersonQuery,
  useDeletePersonMutation,
  useBulkDeletePeopleMutation,
} = peopleApi;
